import type { Chapter } from '@/shared/contracts';
import { useEffect, useRef } from 'react';
import { mmss } from '../lib/format';

export type ChapterLike = Chapter;

type ChapterStripProps = {
  chapters: ChapterLike[];
  active: number;
  onSelect: (chapter: ChapterLike) => void;
  className?: string;
  /** Inside a module's contents panel: no card of its own and no header, the panel names it. */
  embedded?: boolean;
  /** Every chapter is complete (its module's quiz is passed): the nodes turn green and say so. */
  complete?: boolean;
};

/** The chapter with the latest known start at or before `position_s`; the first when none has a start. */
export function chapterAt(chapters: Pick<ChapterLike, 'index' | 'start_s'>[], position_s: number): number {
  let active = chapters[0]?.index ?? 0;
  for (const ch of chapters) if (ch.start_s !== null && ch.start_s <= position_s) active = ch.index;
  return active;
}

/**
 * The chapters as timetable rows: a status node, a number, the title, the start time when the
 * chapter is timed, and the current one marked with the accent tint. Clicking one seeks when its
 * start is known and otherwise scrolls the script to that chapter; the page decides which. The
 * list shows in full, as the handoff's chapter table does, with no scroll inside it (L-37).
 */
export function ChapterStrip({
  chapters,
  active,
  onSelect,
  className = '',
  embedded = false,
  complete = false,
}: ChapterStripProps) {
  const strip = useRef<HTMLElement>(null);
  const arrived = useRef(false);
  useEffect(() => {
    const nav = strip.current;
    const list = nav?.querySelector<HTMLElement>('ol');
    const current = nav?.querySelector<HTMLElement>('[aria-current]');
    if (!nav || !list || !current) return;
    // Once, on arrival, bring the saved chapter into view so a resumed lesson opens where it was
    // left; afterwards keep the current row in view inside this list only, because playback must
    // not pull the page away from the reader.
    if (!arrived.current) {
      arrived.current = true;
      if (current.getBoundingClientRect().bottom > window.innerHeight)
        current.scrollIntoView({ block: 'nearest' });
    }
    if (list.scrollHeight > list.clientHeight)
      list.scrollTop = Math.max(0, current.offsetTop - list.offsetTop - 8);
  }, [active]);
  if (chapters.length === 0) return null;
  return (
    <nav
      ref={strip}
      aria-label="Chapters"
      className={`${embedded ? '' : 'panel'} overflow-hidden ${className}`}
    >
      {!embedded && (
        <div className="flex items-baseline justify-between gap-2 border-b border-rule px-4 py-3 sm:px-5">
          <h2 className="kicker m-0 font-body">Chapters</h2>
          <span className="mono text-[12px] text-inkFaint">{chapters.length}</span>
        </div>
      )}
      <ol className="m-0 list-none divide-y divide-line2 p-0">
        {chapters.map((ch) => {
          const current = ch.index === active;
          return (
            <li key={ch.index}>
              <button
                type="button"
                aria-current={current ? 'true' : undefined}
                onClick={() => onSelect(ch)}
                className={`flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 ease-ea sm:px-5 ${current ? 'bg-accentTint' : 'hover:bg-chip'}`}
              >
                <span
                  aria-hidden="true"
                  className={`h-[10px] w-[10px] shrink-0 rounded-full border-2 ${
                    complete
                      ? 'border-success bg-success'
                      : current
                        ? 'border-accent bg-accent'
                        : 'border-rail bg-panel'
                  }`}
                />
                <span className="mono w-7 shrink-0 text-[12px] text-inkFaint">
                  {String(ch.index + 1).padStart(2, '0')}
                </span>
                <span
                  className={`min-w-0 flex-1 text-[15px] leading-snug ${current ? 'font-medium text-accentText' : 'text-ink'}`}
                >
                  {ch.title}
                </span>
                {complete && <span className="shrink-0 text-[13px] text-successText">Complete</span>}
                {ch.start_s !== null && (
                  <span className="mono shrink-0 text-[13px] text-inkMuted">{mmss(ch.start_s)}</span>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
